import {
  ApprovalStatus,
  formatCurrency,
  UserRole,
} from "@employee-management/common";
import { PaymentStatusToggle } from "./PaymentStatusToggle";
import { EmployeePaymentToggle } from "./EmployeePaymentToggle";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertCircle,
  Calendar,
  Clock,
  DollarSign,
  Activity,
  User,
  FileText,
  History,
} from "lucide-react";
import type { OvertimeEntry } from "@/types/project.types";
import { useAuth } from "@/lib/auth-context";
import { OvertimeApprovalHistory } from "./OvertimeApprovalHistory";
import { Button } from "@/components/ui/button";

interface OvertimeDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overtimeEntry: OvertimeEntry | null;
}

export function OvertimeDetailsDialog({
  open,
  onOpenChange,
  overtimeEntry,
}: OvertimeDetailsDialogProps) {
  const { hasRole } = useAuth();

  if (!overtimeEntry) return null;

  // Determine if user can see financial information
  const canSeeFinancialInfo = hasRole(UserRole.MANAGER);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case ApprovalStatus.PENDING:
        return "secondary";
      case ApprovalStatus.APPROVED:
        return "default";
      case ApprovalStatus.REJECTED:
        return "destructive";
      default:
        return "secondary";
    }
  };

  const calculateTotalHours = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + emp.hours, 0);
  };

  const calculateTotalAmount = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + (emp.total || 0), 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Overtime Entry Details
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Date:</span>
                  <span className="text-sm">
                    {formatDate(overtimeEntry.date)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Total Hours:</span>
                  <span className="text-sm">
                    {calculateTotalHours(overtimeEntry.employees).toFixed(1)}h
                  </span>
                </div>
                {canSeeFinancialInfo && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Total Amount:</span>
                    <span className="text-sm">
                      {formatCurrency(
                        calculateTotalAmount(overtimeEntry.employees)
                      )}
                    </span>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Status:</span>
                  <Badge
                    variant={getStatusBadgeVariant(
                      overtimeEntry.approvalStatus
                    )}
                  >
                    {overtimeEntry.approvalStatus}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Created By:</span>
                  <span className="text-sm">Admin User</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Employees:</span>
                  <span className="text-sm">
                    {overtimeEntry.employees.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Notes
              </h4>
              {overtimeEntry.notes ? (
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md border">
                  {overtimeEntry.notes}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No notes provided
                </p>
              )}
            </div>

            {/* Rejection Comment Section */}
            {overtimeEntry.rejectionComment && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-red-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Rejection Comment
                </h4>
                <div className="bg-red-100/60 dark:bg-red-950/40 rounded-md p-3">
                  <p className="text-red-700 dark:text-red-100 text-sm">
                    {overtimeEntry.rejectionComment}
                  </p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Billing Details Tab */}
          <TabsContent value="billing" className="space-y-4">
            <div className="bg-background rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="!bg-muted/40">
                    <TableHead className="text-sm font-medium">
                      Employee
                    </TableHead>

                    {canSeeFinancialInfo && (
                      <TableHead className="text-sm font-medium text-center">
                        Payment
                      </TableHead>
                    )}
                    <TableHead className="text-sm font-medium text-right">
                      Hours
                    </TableHead>

                    {canSeeFinancialInfo && (
                      <TableHead className="text-sm font-medium text-right">
                        Rate
                      </TableHead>
                    )}
                    {canSeeFinancialInfo && (
                      <TableHead className="text-sm font-medium text-right">
                        Total
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overtimeEntry.employees.map((emp) => (
                    <TableRow
                      key={emp.employee.email}
                      className="border-b-0 hover:bg-transparent"
                    >
                      <TableCell className="py-3">
                        <div>
                          <p className="font-medium text-sm">
                            {emp.employee.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {emp.employee.email}
                          </p>
                        </div>
                      </TableCell>
                      {canSeeFinancialInfo && (
                        <TableCell className="text-center py-3">
                          <EmployeePaymentToggle
                            overtimeEntryId={overtimeEntry.id}
                            employeeId={emp.employee.id}
                            employeeName={emp.employee.name}
                            paymentStatus={emp.paymentStatus}
                            approvalStatus={overtimeEntry.approvalStatus}
                            projectId={overtimeEntry.project.id}
                          />
                        </TableCell>
                      )}
                      <TableCell className="text-right py-3">
                        <span className="text-sm font-medium">
                          {emp.hours}h
                        </span>
                      </TableCell>
                      {canSeeFinancialInfo && (
                        <TableCell className="text-right py-3">
                          <span className="text-sm text-muted-foreground">
                            {formatCurrency(emp.rate)}/hr
                          </span>
                        </TableCell>
                      )}
                      {canSeeFinancialInfo && (
                        <TableCell className="text-right py-3">
                          <span className="text-sm font-medium">
                            {formatCurrency(emp.total)}
                          </span>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="!border-t hover:bg-transparent">
                    <TableCell colSpan={1}>
                      <span className="text-sm font-medium">Total</span>
                    </TableCell>
                    <TableCell colSpan={2} className="text-right py-3">
                      {calculateTotalHours(overtimeEntry.employees).toFixed(1)}h
                    </TableCell>
                    {canSeeFinancialInfo && (
                      <TableCell colSpan={2} className="text-right py-3">
                        {formatCurrency(
                          calculateTotalAmount(overtimeEntry.employees)
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Summary */}
            <div className="bg-muted/50 rounded-md p-4">
              {canSeeFinancialInfo &&
                overtimeEntry.approvalStatus === ApprovalStatus.APPROVED && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Overall Payment Status:
                      </span>
                      <PaymentStatusToggle
                        overtimeEntryId={overtimeEntry.id}
                        paymentStatus={overtimeEntry.paymentStatus}
                        partialPaymentStatus={Boolean(
                          overtimeEntry.employees.find(
                            (employee) => employee.paymentStatus
                          )
                        )}
                        approvalStatus={overtimeEntry.approvalStatus}
                        projectId={overtimeEntry.project.id}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span>
                        {(() => {
                          const paidCount = overtimeEntry.employees.filter(
                            (emp) => emp.paymentStatus
                          ).length;
                          const totalCount = overtimeEntry.employees.length;
                          return `${paidCount} of ${totalCount} employees paid`;
                        })()}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <OvertimeApprovalHistory
              history={overtimeEntry.projectRevisionHistories || []}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            className="!w-full"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
